import { PositionType } from 'data/types';
import {
  distPointToSeg,
  isPolygonInsideFootprint,
  isPolygonSimple,
  polygonArea,
  polygonOverlapsAny
} from './space-polygon';

const FOOTPRINT_EDGE_PROXIMITY_EPS = 1e-3;
const COINCIDENT_VERTEX_EPS = 1e-6;
const INTEGER_PROXIMITY_EPS = 1e-6;
const SHARED_COORD_EPS = 1e-6;
const SEGMENT_AXIS_ALIGN_EPS = 1e-6;
const MIN_POLYGON_AREA = 0.5;
const MAX_FRACTIONAL_GROUPS_FOR_SEARCH = 12;

type SnapMode = 'floor' | 'ceil';
const SNAP_MODES: SnapMode[] = ['floor', 'ceil'];

type Axis = 'x' | 'y';

type VertexAxisLocks = {
  lockX: boolean;
  lockY: boolean;
};

type AxisLocks = {
  lockedX: boolean[];
  lockedY: boolean[];
};

type CoordGroups = {
  groupIndexByVertex: number[];
  representativeByGroup: number[];
  lockedValueByGroup: (number | null)[];
};

type FractionalGroupTarget = {
  axis: Axis;
  groupId: number;
};

export function isVertexOnFootprintEdge(
  vertex: PositionType,
  footprint: PositionType[] | null,
  eps: number = FOOTPRINT_EDGE_PROXIMITY_EPS
): boolean {
  if (!footprint || footprint.length < 3) return false;
  for (let i = 0; i < footprint.length; i += 1) {
    const a = footprint[i];
    const b = footprint[(i + 1) % footprint.length];
    if (distPointToSeg(vertex, a, b) < eps) return true;
  }
  return false;
}

function computeVertexAxisLocks(
  vertex: PositionType,
  footprint: PositionType[] | null
): VertexAxisLocks {
  if (!footprint || footprint.length < 3) {
    return { lockX: false, lockY: false };
  }
  let lockX = false;
  let lockY = false;
  for (let i = 0; i < footprint.length; i += 1) {
    const a = footprint[i];
    const b = footprint[(i + 1) % footprint.length];
    if (distPointToSeg(vertex, a, b) < FOOTPRINT_EDGE_PROXIMITY_EPS) {
      const isHorizontalSegment = Math.abs(a.y - b.y) < SEGMENT_AXIS_ALIGN_EPS;
      const isVerticalSegment = Math.abs(a.x - b.x) < SEGMENT_AXIS_ALIGN_EPS;
      if (isHorizontalSegment && !isVerticalSegment) {
        lockY = true;
      } else if (isVerticalSegment && !isHorizontalSegment) {
        lockX = true;
      } else {
        lockX = true;
        lockY = true;
      }
    }
  }
  return { lockX, lockY };
}

function computeAxisLocks(
  absVerts: PositionType[],
  footprint: PositionType[] | null
): AxisLocks {
  const lockedX: boolean[] = [];
  const lockedY: boolean[] = [];
  for (const v of absVerts) {
    const { lockX, lockY } = computeVertexAxisLocks(v, footprint);
    lockedX.push(lockX);
    lockedY.push(lockY);
  }
  return { lockedX, lockedY };
}

function buildCoordGroups(
  absVerts: PositionType[],
  axis: Axis,
  lockedForAxis: boolean[]
): CoordGroups {
  const groupIndexByVertex = new Array<number>(absVerts.length).fill(-1);
  const representativeByGroup: number[] = [];
  const lockedValueByGroup: (number | null)[] = [];

  for (let i = 0; i < absVerts.length; i += 1) {
    const value = absVerts[i][axis];
    let groupId = -1;
    for (let g = 0; g < representativeByGroup.length; g += 1) {
      if (Math.abs(representativeByGroup[g] - value) < SHARED_COORD_EPS) {
        groupId = g;
        break;
      }
    }
    if (groupId === -1) {
      groupId = representativeByGroup.length;
      representativeByGroup.push(value);
      lockedValueByGroup.push(null);
    }
    groupIndexByVertex[i] = groupId;
    if (lockedForAxis[i]) {
      lockedValueByGroup[groupId] = value;
    }
  }

  return { groupIndexByVertex, representativeByGroup, lockedValueByGroup };
}

function isValueIntegerAlready(value: number): boolean {
  return Math.abs(value - Math.round(value)) < INTEGER_PROXIMITY_EPS;
}

function verticesAreCoincident(a: PositionType, b: PositionType): boolean {
  return (
    Math.abs(a.x - b.x) < COINCIDENT_VERTEX_EPS &&
    Math.abs(a.y - b.y) < COINCIDENT_VERTEX_EPS
  );
}

function hasCoincidentVertices(verts: PositionType[]): boolean {
  for (let i = 0; i < verts.length; i += 1) {
    for (let j = i + 1; j < verts.length; j += 1) {
      if (verticesAreCoincident(verts[i], verts[j])) return true;
    }
  }
  return false;
}

function isValidPolygon(
  candidate: PositionType[],
  footprint: PositionType[] | null,
  otherPolygons: PositionType[][]
): boolean {
  if (candidate.length < 3) return false;
  if (hasCoincidentVertices(candidate)) return false;
  if (polygonArea(candidate) < MIN_POLYGON_AREA) return false;
  if (!isPolygonSimple(candidate)) return false;
  if (
    footprint &&
    footprint.length >= 3 &&
    !isPolygonInsideFootprint(candidate, footprint)
  ) {
    return false;
  }
  return !polygonOverlapsAny(candidate, otherPolygons);
}

function manhattanDisplacement(
  original: PositionType,
  candidate: PositionType
): number {
  return (
    Math.abs(original.x - candidate.x) + Math.abs(original.y - candidate.y)
  );
}

function totalDisplacement(
  original: PositionType[],
  candidate: PositionType[]
): number {
  let total = 0;
  for (let i = 0; i < original.length; i += 1) {
    total += manhattanDisplacement(original[i], candidate[i]);
  }
  return total;
}

function applySnap(value: number, mode: SnapMode): number {
  return mode === 'floor' ? Math.floor(value) : Math.ceil(value);
}

function collectFractionalGroups(
  xGroups: CoordGroups,
  yGroups: CoordGroups
): FractionalGroupTarget[] {
  const result: FractionalGroupTarget[] = [];
  xGroups.representativeByGroup.forEach((rep, groupId) => {
    if (xGroups.lockedValueByGroup[groupId] !== null) return;
    if (isValueIntegerAlready(rep)) return;
    result.push({ axis: 'x', groupId });
  });
  yGroups.representativeByGroup.forEach((rep, groupId) => {
    if (yGroups.lockedValueByGroup[groupId] !== null) return;
    if (isValueIntegerAlready(rep)) return;
    result.push({ axis: 'y', groupId });
  });
  return result;
}

function buildDefaultGroupValues(groups: CoordGroups): number[] {
  return groups.representativeByGroup.map((rep, groupId) => {
    const lockedValue = groups.lockedValueByGroup[groupId];
    if (lockedValue !== null) return lockedValue;
    if (isValueIntegerAlready(rep)) return Math.round(rep);
    return rep;
  });
}

function buildRoundedGroupValues(groups: CoordGroups): number[] {
  return groups.representativeByGroup.map((rep, groupId) => {
    const lockedValue = groups.lockedValueByGroup[groupId];
    if (lockedValue !== null) return lockedValue;
    return Math.round(rep);
  });
}

function applyGroupValuesToVertices(
  absVerts: PositionType[],
  xGroups: CoordGroups,
  yGroups: CoordGroups,
  xValues: number[],
  yValues: number[]
): PositionType[] {
  return absVerts.map((_v, i) => ({
    x: xValues[xGroups.groupIndexByVertex[i]],
    y: yValues[yGroups.groupIndexByVertex[i]]
  }));
}

function buildCandidateForComboIndex(
  absVerts: PositionType[],
  xGroups: CoordGroups,
  yGroups: CoordGroups,
  fractionalGroups: FractionalGroupTarget[],
  comboIndex: number
): PositionType[] {
  const xValues = buildDefaultGroupValues(xGroups);
  const yValues = buildDefaultGroupValues(yGroups);

  let remaining = comboIndex;
  for (const target of fractionalGroups) {
    const mode = SNAP_MODES[remaining % SNAP_MODES.length];
    remaining = Math.floor(remaining / SNAP_MODES.length);
    const groups = target.axis === 'x' ? xGroups : yGroups;
    const rep = groups.representativeByGroup[target.groupId];
    const snapped = applySnap(rep, mode);
    if (target.axis === 'x') {
      xValues[target.groupId] = snapped;
    } else {
      yValues[target.groupId] = snapped;
    }
  }

  return applyGroupValuesToVertices(
    absVerts,
    xGroups,
    yGroups,
    xValues,
    yValues
  );
}

export function snapPolygonVerticesToGrid(
  absVerts: PositionType[],
  footprint: PositionType[] | null,
  otherPolygons: PositionType[][]
): PositionType[] | null {
  if (absVerts.length < 3) return null;

  const { lockedX, lockedY } = computeAxisLocks(absVerts, footprint);
  const xGroups = buildCoordGroups(absVerts, 'x', lockedX);
  const yGroups = buildCoordGroups(absVerts, 'y', lockedY);
  const fractionalGroups = collectFractionalGroups(xGroups, yGroups);

  if (fractionalGroups.length === 0) {
    return null;
  }

  const roundedCandidate = applyGroupValuesToVertices(
    absVerts,
    xGroups,
    yGroups,
    buildRoundedGroupValues(xGroups),
    buildRoundedGroupValues(yGroups)
  );
  if (isValidPolygon(roundedCandidate, footprint, otherPolygons)) {
    return roundedCandidate;
  }

  if (fractionalGroups.length > MAX_FRACTIONAL_GROUPS_FOR_SEARCH) {
    return null;
  }

  const totalCombinations = SNAP_MODES.length ** fractionalGroups.length;
  let best: PositionType[] | null = null;
  let bestDisplacement = Infinity;
  for (let combo = 0; combo < totalCombinations; combo += 1) {
    const candidate = buildCandidateForComboIndex(
      absVerts,
      xGroups,
      yGroups,
      fractionalGroups,
      combo
    );
    if (isValidPolygon(candidate, footprint, otherPolygons)) {
      const displacement = totalDisplacement(absVerts, candidate);
      if (displacement < bestDisplacement) {
        bestDisplacement = displacement;
        best = candidate;
      }
    }
  }
  return best;
}
